import { Component, HostListener, Input, NgZone, ViewChild } from '@angular/core';
import { ApiRoutes, Authentication } from '@sb/library/api';
import { DesignBlockImageScope, DesignBlockScope, Environment, ErrorMessages, Field, FormBase, LoginFormMode, Optional, Required, Scope, WebsiteMode, WebsitePageScope, boolean, string } from '@sb/library/base';
import { ButtonComponent } from '@sb/library/button';
import { ErrorCode, IErrorResponse, PasswordResetInputItem, PortalType, PreauthenticationResultItem, UserAuthenticationInputItem, UserPreauthenticationInputItem, WebsiteBlockLoginFormDisplayItem, WebsiteBlockLoginFormType, WebsiteBlockTextAndMediaImageDisplayItem, WebsiteRenderMode } from '@sb/library/data-models';
import { RecaptchaComponent } from '@sb/library/recaptcha';
import { CookieService } from 'ngx-cookie-service';
import { DesignBlockIframeBaseViewComponent } from '../design-block-iframe-base-view.component';

export class LoginFormDesignBlockScope extends Scope {
    websiteBlockData = Field<WebsiteBlockLoginFormDisplayItem>();
    imageData = Field<WebsiteBlockTextAndMediaImageDisplayItem>();
}

class LoginFormForm extends FormBase {
    EmailAddress = Required(string);
    Password = Required(string);
    PublicComputer = Optional(boolean);
}

class MFAForm extends FormBase {
    Code = Required(string);
}

class ResetPasswordForm extends FormBase {
    EmailAddress = Required(string);
}

class MultipleAccountSelectionForm extends FormBase {
    AccountSelection = Required(PreauthenticationResultItem);
}

enum LoginWidgetNextStep {
    Login = 'Login',
    ResetPassword = 'ResetPassword',
}

@Component({
    selector: 'lib-login-form-design-block',
    templateUrl: './login-form-design-block.component.html',
    styleUrls: ['./login-form-design-block.component.scss'],
})
export class LoginFormDesignBlockComponent extends DesignBlockIframeBaseViewComponent implements DesignBlockScope, DesignBlockImageScope {
    override Scope = this.SetScope({
        Implements: [DesignBlockScope, DesignBlockImageScope],
        Requires: WebsitePageScope,
    });

    @ViewChild('recaptcha') recaptchaRef!: RecaptchaComponent;
    @ViewChild('loginButton', { static: false }) loginRef!: ButtonComponent;
    @ViewChild('continueButton', { static: false }) continueRef!: ButtonComponent;
    @ViewChild('continueMFAButton', { static: false }) continueMfaRef!: ButtonComponent;

    @Input() websiteBlockData!: WebsiteBlockLoginFormDisplayItem;

    readonly loginForm = LoginFormForm.Create(false);
    readonly mfaForm = MFAForm.Create(false);
    readonly resetPasswordForm = ResetPasswordForm.Create(false);
    readonly multipleAccountSelectionForm = MultipleAccountSelectionForm.Create(false);

    private _apiInProgress = false;
    private _availableAccounts: PreauthenticationResultItem[] = [];
    private accountForMFA!: PreauthenticationResultItem;

    private _loginFormMode: LoginFormMode = LoginFormMode.Normal;

    private _localDeployment: boolean = false;
    private _buttonElement: any = null;

    private _EnableButton = this.EnableButton.bind(this);

    private _loginWidgetNextStep: LoginWidgetNextStep = LoginWidgetNextStep.Login;

    private _alreadyLoggedInFirstName!: string;
    private _alreadyLoggedInSchoolName: string = '';
    private _portalType!: PortalType;

    private _manualRedirectJWT: string = '';

    readonly emailError: ErrorMessages = {
        required: (_) => $localize`:@@LoginFormEmailMissingError:Please enter an email address`,
    };

    readonly passwordError: ErrorMessages = {
        required: (_) => $localize`:@@LoginFormPasswordMissingError:Please enter a password`,
        api: (_) => $localize`:@@LoginFormPasswordAPIError:Sorry, you've entered an incorrect email address or password. Please try again.`,
    };

    readonly continueError: ErrorMessages = {
        required: (_) => $localize`:@@LoginFormJWTSelectionMissingError:Please select an account to use`,
    };

    constructor(
        private _Environment: Environment,
        private _CookieService: CookieService,
        private _NgZone: NgZone
    ) {
        super();

        if (_Environment.Application.BaseUrls.Api!.includes('localhost')) {
            this._localDeployment = true;
        }
    }

    override ngOnInit(): void {
        super.ngOnInit();

        this.resetPasswordForm.isViewOnly = this.isViewOnly;
        this.loginForm.isViewOnly = this.isViewOnly;
        this.multipleAccountSelectionForm.isViewOnly = this.isViewOnly;

        if (this.Scope.renderContext.RenderMode == WebsiteRenderMode.Widget) {
            this.InitializeLoginWidgetState();
        }
    }

    override ngOnDestroy(): void {
        super.ngOnDestroy();

        if (this._iframe && this._iframe.iFrameResizer) {
            this._iframe.iFrameResizer.close();
        }
    }

    private InitializeLoginWidgetState(): void {
        this._loginFormMode = LoginFormMode.Normal;

        const studentCookie = this._CookieService.get('StudentPortalAuth');
        const teacherCookie = this._CookieService.get('TeacherPortalAuth');

        if (this.Scope.websiteMode === WebsiteMode.Live && (studentCookie || teacherCookie)) {
            let decodedJwt: Authentication;

            if (teacherCookie) {
                this._portalType = PortalType.TeacherPortal;
                decodedJwt = Authentication.DecodeJWT(teacherCookie)!;
            } else {
                this._portalType = PortalType.StudentPortal;
                decodedJwt = Authentication.DecodeJWT(studentCookie)!;
            }

            this._alreadyLoggedInFirstName = decodedJwt.FirstName;
            this._alreadyLoggedInSchoolName = decodedJwt.SchoolName ?? '';
            this._loginFormMode = LoginFormMode.AlreadyLoggedIn;
        }
    }

    LoginButton(): void {
        this.recaptchaRef.GetCaptcha(this.Scope.renderContext.LoginFormRenderContext?.CaptchaRequired!);
        this._loginWidgetNextStep = LoginWidgetNextStep.Login;
    }

    HandleCAPTCHA() {
        switch (this._loginWidgetNextStep) {
            case LoginWidgetNextStep.Login:
                this.Login();
                break;
            case LoginWidgetNextStep.ResetPassword:
                this.ResetPasswordAPI();
                break;
        }
    }

    HandleCAPTCHAError() {
        this.EnableButton();
        this.resetPasswordForm.enable();
    }

    Login(): void {
        this.loginForm.CheckValidity().subscribe((result) => {
            if (result) {
                const inputItem = new UserPreauthenticationInputItem({
                    EmailAddress: this.loginForm.controls.EmailAddress.value,
                    Password: this.loginForm.controls.Password.value,
                    Captcha: this.recaptchaRef.resolvedRecaptchaToken,
                });

                const routeURL = this._Environment.Application.BaseUrls.Api + 'preauth';

                try {
                    const xhr = new XMLHttpRequest();
                    xhr.open('POST', routeURL, false); // Synchronous request
                    xhr.setRequestHeader('Content-Type', 'application/json;charset=UTF-8');

                    xhr.onreadystatechange = () => this.PreAuthRequestReadyStateChange(xhr);
                    xhr.send(JSON.stringify(inputItem));
                } catch {
                    const xhr = new XMLHttpRequest();

                    xhr.open('POST', routeURL, true); // Synchronous request didn't work; try async
                    xhr.setRequestHeader('Content-Type', 'application/json;charset=UTF-8');

                    xhr.onreadystatechange = () => this.PreAuthRequestReadyStateChange(xhr);
                    xhr.send(JSON.stringify(inputItem));
                }
            } else {
                this.EnableButton();
            }
        });
    }

    private PreAuthRequestReadyStateChange(xhr: XMLHttpRequest) {
        if (xhr.readyState != 4) {
            // Wait for DONE state
            return;
        }

        this._NgZone.run(() => {
            if (xhr.status == 200) {
                const matchingAccountsForSchool = JSON.parse(xhr.responseText) as PreauthenticationResultItem[];
                //TODO: we need login widget settings to see if we want to filter by school or not.
                //const matchingAccountsForSchool = (JSON.parse(xhr.responseText) as PreauthenticationResultItem[]).filter(e => e.SchoolID === this._data.SchoolID);

                if (matchingAccountsForSchool.length > 1) {
                    this._loginFormMode = LoginFormMode.MultipleJWTs;
                    this._availableAccounts = matchingAccountsForSchool;
                    matchingAccountsForSchool.forEach((account) => (account.ID = account.ProfileID!));

                    setTimeout(() => {
                        this.multipleAccountSelectionForm.controls.AccountSelection.setValue(matchingAccountsForSchool[0]); //init it to the first option, as string because 0 = false for a value.
                    });
                } else if (matchingAccountsForSchool.length === 1) {
                    this.AccountSelected(matchingAccountsForSchool[0]);
                } else {
                    this.recaptchaRef.ResetCaptchaToken();
                    this.loginForm.enable();
                    this.EnableButton();
                    this.loginForm.controls.Password.SetApiError();
                }
            } else if ((JSON.parse(xhr.response) as IErrorResponse).ErrorCode == ErrorCode.Captcha) {
                this.recaptchaRef.ResetCaptchaToken();
                this.Scope.renderContext.LoginFormRenderContext!.CaptchaRequired = true;
                this.recaptchaRef.GetCaptcha(true);
            } else {
                this.recaptchaRef.ResetCaptchaToken();
                this.loginForm.enable();
                this.EnableButton();
            }
        });
    }

    Normal(): void {
        this._loginFormMode = LoginFormMode.Normal;

        this.loginForm.reset();
        this.loginForm.enable();

        this.mfaForm.reset();
        this.mfaForm.enable();

        this.recaptchaRef.ResetCaptchaToken();

        this.resetPasswordForm.reset();
        this.resetPasswordForm.enable();
    }

    Forgot(): void {
        this._loginFormMode = LoginFormMode.ForgotPassword;
    }

    ResetPasswordButton(): void {
        this.resetPasswordForm.CheckValidity().subscribe((result) => {
            if (result) {
                this.recaptchaRef.GetCaptcha(true);
                this._loginWidgetNextStep = LoginWidgetNextStep.ResetPassword;
            }
        });
    }

    ResetPasswordAPI(): void {
        ApiRoutes.Authentication.LoginEmail.Send.Call({
            Body: new PasswordResetInputItem({
                EmailAddress: this.resetPasswordForm.controls.EmailAddress.value,
                Captcha: this.recaptchaRef.resolvedRecaptchaToken,
            }),
        }).subscribe({
            next: () => {
                this.resetPasswordForm.enable();
                this._loginFormMode = LoginFormMode.ResetPasswordThankYou;
            },

            error: (error: typeof ApiRoutes.Authentication.LoginEmail.Send.Error) => {
                if (error.Response.ErrorCode == ErrorCode.Captcha) {
                    this.recaptchaRef.ResetCaptchaToken();
                    this.Scope.renderContext.LoginFormRenderContext!.CaptchaRequired = true;
                    this.recaptchaRef.GetCaptcha(true);
                    this.resetPasswordForm.enable();
                } else {
                    throw error;
                }
            },
        });
    }

    Continue(): void {
        this.multipleAccountSelectionForm.CheckValidity().subscribe((result) => {
            if (result) {
                this.AccountSelected(this.multipleAccountSelectionForm.controls.AccountSelection.value!);
            } else {
                this.EnableButton();
            }
        });
    }

    AccountSelected(account: PreauthenticationResultItem): void {
        if (account.MfaRequired || account.OtpRequired) {
            this.multipleAccountSelectionForm.enable();
            this.EnableButton();
            this.accountForMFA = account;
            this._loginFormMode = account.MfaRequired ? LoginFormMode.MfaRequired : LoginFormMode.OtpRequired;
        } else {
            this.RedirectToPortal(account);
        }
    }

    ContinueMFA(): void {
        this.mfaForm.CheckValidity().subscribe(result => {
            if (result) {
                this.RedirectToPortal(this.accountForMFA, this.mfaForm.controls.Code.value);
            } else {
                this.EnableButton();
            }
        });
    }

    Logout(): void {
        this._loginFormMode = LoginFormMode.Normal;
    }

    ReturnToPortal(): void {
        //we are already logged in!
        this.RedirectToTargetURL(this.redirectRoute);
    }

    RedirectToPortal(jwt: PreauthenticationResultItem, mfaCode: string | null = null): void {
        this._apiInProgress = true;
        this._manualRedirectJWT = '';

        //need to get the jwt string
        const inputItem = new UserAuthenticationInputItem({
            EmailAddress: this.loginForm.controls.EmailAddress.value,
            Password: this.loginForm.controls.Password.value,
            PublicComputer: this.loginForm.controls.PublicComputer.value,
            ProfileID: jwt.ProfileID,
            MFAorOTP: mfaCode
        });

        const routeURL = this._Environment.Application.BaseUrls.Api + 'auth';

        try {
            const xhr = new XMLHttpRequest();
            xhr.open('POST', routeURL, false); // Synchronous request
            xhr.setRequestHeader('Content-Type', 'application/json;charset=UTF-8');

            xhr.onreadystatechange = () => this.AuthRequestReadyStateChange(xhr, jwt);
            xhr.send(JSON.stringify(inputItem));
        } catch {
            const xhr = new XMLHttpRequest();

            xhr.open('POST', routeURL, true); // Synchronous request didn't work; try async
            xhr.setRequestHeader('Content-Type', 'application/json;charset=UTF-8');

            xhr.onreadystatechange = () => this.AuthRequestReadyStateChange(xhr, jwt);
            xhr.send(JSON.stringify(inputItem));
        }
    }

    private AuthRequestReadyStateChange(xhr: XMLHttpRequest, jwt: PreauthenticationResultItem) {
        if (xhr.readyState != 4) {
            // Wait for DONE state
            return;
        }

        this._NgZone.run(() => {
            //either we are logged in, or we are unauthorized.
            if (xhr.status === 200) {
                const jwtString = JSON.parse(xhr.responseText) as string;

                this._manualRedirectJWT = this.redirectRoute;
                if (jwt) {
                    this._manualRedirectJWT += '?Jwt=' + jwtString;
                }

                this.RedirectToTargetURL(this._manualRedirectJWT);

                setTimeout(() => {
                    this._loginFormMode = LoginFormMode.ManualRedirect;
                }, 3000);
            } else {
                this.mfaForm.enable();
                this.mfaForm.controls.Code.SetApiError();
            }
        });
    }

    RedirectToTargetURL(targetURL: string) {
        //Sites that use sandboxing don't allow the browser to redirect,
        //so we have to open in a new tab/window instead of redirecting

        if (this.Scope.renderContext.WidgetSandboxed) {
            window.open(targetURL, '_blank');
        } else {
            window.open(targetURL, '_top');
        }
    }

    // this function only disable the button on mousedown
    DisableButton(buttonComponent: ButtonComponent) {
        this._buttonElement = buttonComponent.button!.nativeElement;

        this._buttonElement.style.transition = 'none';
        this._buttonElement.classList.add('disabled');
        this._buttonElement.style.pointerEvents = 'auto';

        document.addEventListener('mouseup', this._EnableButton);
        this._buttonElement.addEventListener('mouseleave', this._EnableButton);
    }

    EnableButton() {
        if (this._buttonElement) {
            const buttonElement = this._buttonElement;
            this._buttonElement = null;
            document.removeEventListener('mouseup', this._EnableButton);

            setTimeout(() => {
                buttonElement.classList.remove('disabled');
                buttonElement.removeEventListener('mouseleave', this._EnableButton);
            }, 500);
        }
    }

    get kind(): WebsiteBlockLoginFormType {
        return this.websiteBlockData.LoginFormType;
    }

    get redirectRoute(): string {
        return this._localDeployment ? `${origin}/schoolbox2.3/LoginRedirect.ashx` : `${origin}/LoginRedirect.ashx`;
    }

    get ProfileMode(): string {
        return Authentication.ProfileMode;
    }

    get PortalType(): typeof PortalType {
        return PortalType;
    }

    get apiInProgress(): boolean {
        return this._apiInProgress;
    }

    get WebsiteBlockLoginFormType(): typeof WebsiteBlockLoginFormType {
        return WebsiteBlockLoginFormType;
    }

    get LoginFormMode(): typeof LoginFormMode {
        return LoginFormMode;
    }

    get loginFormMode(): LoginFormMode {
        return this._loginFormMode;
    }

    get availableAccounts(): PreauthenticationResultItem[] {
        return this._availableAccounts;
    }

    get alreadyLoggedInFirstName(): string {
        return this._alreadyLoggedInFirstName;
    }

    get alreadyLoggedInSchoolName(): string {
        return this._alreadyLoggedInSchoolName;
    }

    get portalType(): string {
        return this._portalType;
    }

    get manualRedirectJWT(): string {
        return this._manualRedirectJWT;
    }

    get imageData(): WebsiteBlockTextAndMediaImageDisplayItem {
        return this.websiteBlockData.Images[0];
    }

    @HostListener('keydown.enter')
    onKeydownHandler() {
        if (this.Scope.websiteMode === WebsiteMode.Live) {
            (document.activeElement as HTMLElement).blur();
            switch (this._loginFormMode) {
                case LoginFormMode.Normal:
                    //needs (mousedown)="DisableButton(loginButton)"
                    this.DisableButton(this.loginRef);
                    setTimeout(() => {
                        this.LoginButton();
                    });
                    break;
                case LoginFormMode.AlreadyLoggedIn:
                    this.ReturnToPortal();
                    break;
                case LoginFormMode.ForgotPassword:
                    this.ResetPasswordButton();
                    break;
                case LoginFormMode.MultipleJWTs:
                    //needs (mousedown)="DisableButton(continueButton)"
                    this.DisableButton(this.continueRef);
                    setTimeout(() => {
                        this.Continue();
                    });
                    break;
                case LoginFormMode.MfaRequired:
                    //needs (mousedown)="DisableButton(continueMFAButton)"
                    this.DisableButton(this.continueMfaRef);
                    setTimeout(() => {
                        this.ContinueMFA();
                    });
                    break;
                case LoginFormMode.ResetPasswordThankYou:
                    this.Normal();
                    break;
            }
        }
    }
}
